/**
 * Supreme Master Engine Kernel
 * Manages 120Hz fixed physics updates, state machine, scene switching, and render interpolation.
 */

import { EventBus, EventType } from './EventBus';
import { StateStore } from '../state/Store';
import { VerletKernel } from '../physics/VerletKernel';
import { InputSystem } from '../input/InputSystem';
import { AudioSystem } from '../audio/AudioSystem';
import { RenderPipeline } from '../renderer/RenderPipeline';
import { CameraSpline, CameraState } from '../renderer/CameraSpline';

import { ISceneLifecycle } from '../scenes/ISceneLifecycle';
import { Scene01_Ignition } from '../scenes/Scene01_Ignition';
import { Scene02_Monolith } from '../scenes/Scene02_Monolith';
import { Scene03_Alignment } from '../scenes/Scene03_Alignment';
import { Scene04_CollapseBridge } from '../scenes/Scene04_CollapseBridge';
import { Scene05_Anvil } from '../scenes/Scene05_Anvil';
import { Scene06_LeverOcean } from '../scenes/Scene06_LeverOcean';
import { Scene07_Clockwork } from '../scenes/Scene07_Clockwork';
import { Scene08_Dissolve } from '../scenes/Scene08_Dissolve';
import { Scene09_CyberImplosion } from '../scenes/Scene09_CyberImplosion';
import { Scene10_EmberReborn } from '../scenes/Scene10_EmberReborn';

export enum EngineState {
  UNINITIALIZED = 0,
  ALLOCATING = 1,
  READY = 2,
  RUNNING = 3,
  PAUSED = 4,
  DISPOSING = 5,
  TERMINATED = 6
}

export class Engine {
  private static instance: Engine;
  private state: EngineState = EngineState.UNINITIALIZED;

  private eventBus: EventBus;
  private store: StateStore;
  private physics: VerletKernel;
  private input: InputSystem;
  private audio: AudioSystem;
  private renderer: RenderPipeline | null = null;
  private camera: CameraSpline;

  private scenes: ISceneLifecycle[] = [];
  private activeSceneIndex: number = 0;

  // 120Hz Loop Timing
  private lastFrameTime: number = 0;
  private accumulator: number = 0;
  private animFrameId: number | null = null;

  // FPS Telemetry
  private frameCount: number = 0;
  private fpsTimer: number = 0;
  private currentFPS: number = 60;

  private constructor() {
    this.state = EngineState.ALLOCATING;

    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
    this.physics = new VerletKernel();
    this.input = new InputSystem();
    this.audio = new AudioSystem();
    this.camera = new CameraSpline();

    // Register all 10 scenes
    this.scenes = [
      new Scene01_Ignition(),
      new Scene02_Monolith(),
      new Scene03_Alignment(),
      new Scene04_CollapseBridge(),
      new Scene05_Anvil(),
      new Scene06_LeverOcean(),
      new Scene07_Clockwork(),
      new Scene08_Dissolve(),
      new Scene09_CyberImplosion(),
      new Scene10_EmberReborn()
    ];
  }

  public static getInstance(): Engine {
    if (!Engine.instance) {
      Engine.instance = new Engine();
    }
    return Engine.instance;
  }

  public init(canvas: HTMLCanvasElement): void {
    if (this.state !== EngineState.ALLOCATING) return;

    this.renderer = new RenderPipeline(canvas);
    this.input.init(canvas);

    // Bind window resize
    window.addEventListener('resize', () => {
      if (this.renderer) this.renderer.resize();
    });

    // Subscribe to Scene Change Requests
    this.eventBus.subscribe(EventType.SCENE_CHANGE_REQUEST, (payload) => {
      if (payload.sceneId !== undefined && payload.sceneId >= 0 && payload.sceneId <= 9) {
        this.switchScene(payload.sceneId);
      }
    });

    // Mount and activate Scene 01
    const scene01 = this.scenes[0];
    scene01.mount();
    scene01.activate();
    this.activeSceneIndex = 0;

    this.state = EngineState.READY;
  }

  public start(): void {
    if (this.state !== EngineState.READY && this.state !== EngineState.PAUSED) return;

    this.state = EngineState.RUNNING;
    this.lastFrameTime = performance.now();
    this.fpsTimer = performance.now();
    this.accumulator = 0;

    this.loop(this.lastFrameTime);
  }

  public pause(): void {
    if (this.state === EngineState.RUNNING) {
      this.state = EngineState.PAUSED;
      if (this.animFrameId !== null) {
        cancelAnimationFrame(this.animFrameId);
        this.animFrameId = null;
      }
    }
  }

  public switchScene(sceneIndex: number): void {
    if (sceneIndex === this.activeSceneIndex || sceneIndex < 0 || sceneIndex >= this.scenes.length) {
      return;
    }

    const prevScene = this.scenes[this.activeSceneIndex];
    prevScene.deactivate();
    prevScene.unmount();

    this.activeSceneIndex = sceneIndex;
    this.store.setSceneId(sceneIndex);
    this.camera.setTargetScene(sceneIndex);

    const nextScene = this.scenes[sceneIndex];
    nextScene.mount();
    nextScene.activate();

    this.eventBus.emit(
      EventType.SCENE_TRANSITION_COMPLETE,
      0, 0, 0, 0, 0, 1, sceneIndex,
      `Entered Epoch: ${nextScene.era}`
    );
  }

  public getActiveScene(): ISceneLifecycle {
    return this.scenes[this.activeSceneIndex];
  }

  public getFPS(): number {
    return this.currentFPS;
  }

  public getAudioSystem(): AudioSystem {
    return this.audio;
  }

  private loop = (currentTime: number): void => {
    if (this.state !== EngineState.RUNNING) return;

    const frameDelta = (currentTime - this.lastFrameTime) / 1000; // seconds
    this.lastFrameTime = currentTime;

    // Cap delta to 0.1s to prevent spiral-of-death on background tab lag
    const cappedDelta = Math.min(0.1, frameDelta);
    this.accumulator += cappedDelta;

    // 120Hz Fixed Physics Update Sub-Step Accumulator Loop
    let updateSteps = 0;
    const maxSteps = 4; // Spiral of death cap

    while (this.accumulator >= VerletKernel.FIXED_DELTA && updateSteps < maxSteps) {
      this.update(VerletKernel.FIXED_DELTA);
      this.accumulator -= VerletKernel.FIXED_DELTA;
      updateSteps++;
    }

    if (updateSteps >= maxSteps) {
      this.accumulator = 0; // Prevent accumulator overflow
    }

    // Camera spline and render frame
    const cameraState = this.camera.update(frameDelta);
    this.render(cameraState);

    // Compute FPS
    this.frameCount++;
    if (currentTime - this.fpsTimer >= 1000) {
      this.currentFPS = this.frameCount;
      this.frameCount = 0;
      this.fpsTimer = currentTime;
      this.eventBus.emit(EventType.TELEMETRY_FPS_UPDATE, 0, 0, 0, 0, this.currentFPS);
    }

    this.animFrameId = requestAnimationFrame(this.loop);
  };

  private update(dt: number): void {
    // 1. Update Physics Kernel
    this.physics.updateParticles(dt);

    // 2. Update Active Scene
    const activeScene = this.scenes[this.activeSceneIndex];
    if (activeScene) {
      activeScene.update(dt);
    }
  }

  private render(camera: CameraState): void {
    if (!this.renderer) return;

    const activeScene = this.scenes[this.activeSceneIndex];
    let lightIntensity = 0;
    let lightPos: [number, number] = [0, 0];

    if (this.activeSceneIndex === 0 && activeScene instanceof Scene01_Ignition) {
      const illum = activeScene.getIllumination();
      lightIntensity = illum.intensity;
      lightPos = illum.pos;
    } else {
      lightIntensity = 0.6;
      lightPos = [0, 0];
    }

    // Clear Screen
    this.renderer.clear();

    // Cavern slate background pass
    this.renderer.renderCavernBackground(lightPos[0], lightPos[1], lightIntensity, this.activeSceneIndex);

    // Render Scene Custom Canvas Details
    if (activeScene) {
      const canvas = this.renderer['canvas'];
      const ctx = this.renderer['ctx'];
      activeScene.render(ctx, canvas.width / (window.devicePixelRatio || 1), canvas.height / (window.devicePixelRatio || 1), camera);
    }

    // Render Particle Embers
    this.renderer.renderParticles(camera);

    // Floating Dust
    this.renderer.renderAmbientDust(performance.now() * 0.001);

    // Vignette Post-processing
    this.renderer.renderVignette();
  }
}
